import { catchRenderException } from '@philips-emr/hpms-components';
import { ButtonAPI } from '@philips-emr/tws-components';
import { ThemeTonalRange } from '@philips-emr/tws-constants';
import { ClickEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, ModalService } from '@philips-emr/tws-core/lib/services';
import { ResponseBodyMapperType } from '@philips-emr/tws-core/lib/services/http-service';
import { ModalProps } from '@philips-emr/tws-core/lib/services/modal-service';

import { FragmentNames } from '../codegen';
import { Endpoints } from '../definitions/endpoints';

import { ToastService } from './toast-service';

interface Report {
  cdClassifRelat: string,
  cdRelatorio: string,
  type?: string;
}

interface ReportParameters {
  cdGrupo: string;
  nrSeqBeneficiario: number;
  endpoint?: string;
  parametrosEndpoint?: {};
  parametrosRelatorio?: {};
  record?: Report[];
}

class ReportService {
  static abreRelatrio: any;

  static print(param: ReportParameters): Promise<{ event: (event: ClickEvent) => void, record: Report[] }> {
    return new Promise((resolve, reject) => {

      const { cdGrupo, nrSeqBeneficiario } = param;

      if (!param.endpoint) {
        param.endpoint = Endpoints.GET.RELATORIOS;
      }

      HttpService.get<Report[]>(param.endpoint, { ...param.parametrosEndpoint, cdGrupo, nrSeqBeneficiario })
        .then(({ body: record }) => {
          if (record && record.length) {
            return resolve({ event: (event) => this.open({ ...param, record }, event), record });
          }
          reject();
        })
        .catch((response) => ToastService.open(response));
    });
  }

  static open(param: ReportParameters, event?: ClickEvent): void {
    const api = event?.target as ButtonAPI;
    const { record } = param;

    catchRenderException(() => api.setState({ loading: true }));

    (record && record.length > 1 ? this.openModal : this.openReport)(param)
      .finally(() => catchRenderException(() => api.setState({ loading: false })));
  }

  static openReport({ nrSeqBeneficiario, cdGrupo, parametrosRelatorio, record }: ReportParameters): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!record) {
        reject();
        return;
      }

      const { cdRelatorio, cdClassifRelat } = record[0];
      const body = {
        code: cdRelatorio,
        parameters: parametrosRelatorio ?? {},
        type: cdClassifRelat,
      };

      HttpService.post(Endpoints.POST.RELATORIO, { nrSeqBeneficiario, cdGrupo }, { body, responseBodyMapperType: ResponseBodyMapperType.BLOB })
        .then(response => {
          if (response.body) {
            const { type } = response.body as Report;
            const file = new Blob([response.body as Blob], { type });

            if (type === 'application/json') {
              void file.text().then((resp) => {
                openReportModal(resp);
              });
            } else {
              const file = new Blob([response.body as Blob], { type: 'application/pdf' });
              const url = URL.createObjectURL(file);
              const win = window.open(url, '_blank');
              win && win.focus();
            }
            resolve();
          }
          reject();
        })
        .catch(response => {
          ToastService.open(response);
          reject();
        });
    });
  }

  private static openModal(param: ReportParameters): Promise<void> {
    return new Promise((resolve,) => ModalService.openDefault({
      closeable: true,
      onClose: () => resolve(),
      contextData: { ...param },
      fragment: FragmentNames.MODAL_RELATORIO,
      theme: ThemeTonalRange.ULTRA_LIGHT,
      title: 'beneficiary.relatorios',
    }));
  }
}

function openReportModal(src: string): void {
  const modalWithFragment: ModalProps = {
    closeable: true,
    fragment: FragmentNames.RELATORIO_PDF,
    contextData: { src },
  };
  ModalService.openFullscreen(modalWithFragment);
}

export {
  Report,
  ReportParameters,
  ReportService,
};
